import { useState, useEffect } from 'react';
import {
  Button,
  Cascader,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Spin,
  Upload,
  message,
  Card,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRequest } from 'ahooks';
import qs from 'query-string';
import moment from 'moment/moment';
import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import { getArea, getDictType } from '@/services/common';
import {
  addApply,
  uploadFileUrl,
  getProjectName,
  getApplicantList,
  getApplicationDetail,
  updateBackApply,
} from '@/services/supplyChainFinance';
import {
  SPC_COMPANY_ROLE,
} from '@/utils/mappingDict';
import './index.less';
import ContactInfo from './components/ContactInfo';
import AddEnterpriseModal from './components/AddEnterpriseModal';
import { getFileListVal } from '@/utils/fileDataUtil';

const formItemLayout = {
  labelCol: {
    sm: { span: 8 },
  },
  wrapperCol: {
    sm: { span: 16 },
  },
};

const formItemLayout1 = {
  labelCol: {
    sm: { span: 6 },
  },
  wrapperCol: {
    sm: { span: 18 },
  },
};

const titleMap = {
  1: '链主企业申报',
  2: '链属企业申报',
  3: '相关经营主体申报',
};

const defaultParams = [SPC_COMPANY_ROLE].join(',');

export default function Application() {
  const [form] = Form.useForm();
  const Navigate = useNavigate();
  const { search } = useLocation();
  const {
    backId, type, billionAgriProjectId, reDeclare,
  } = qs.parse(search);
  const { userType } = useSelector((state) => state);
  const user = useSelector((state) => state.userInfo);
  const [authInfoModal, setAuthInfoModal] = useState(false); // 授权书内容
  const [cooperationAgreementFiles, setCooperationAgreementFiles] = useState([]); // 与关联企业合作协议
  const [appendixFiles, setAppendixFiles] = useState([]); // 其他必要材料
  const [isDown, setIsDown] = useState(undefined); // 是否有下游企业
  const [isUp, setIsUp] = useState(undefined); // 是否有上游企业
  const [selectedDownstream, setSelectedDownstream] = useState([]); // 选中的下游企业
  const [selectedUpstream, setSelectedUpstream] = useState([]); // 选中的上游企业
  const [enterpriseList, setEnterpriseList] = useState([]); // 上下游企业列表
  const [chainBusinessInfoMap, setChainBusinessInfoMap] = useState({}); // 业务往来信息映射表（按 applicantId 索引）
  const [addEnterpriseModal, setAddEnterpriseModal] = useState(false); // 添加企业弹窗
  const [addEnterpriseType, setAddEnterpriseType] = useState(''); // 添加企业类型：'downstream' 或 'upstream'
  const [addEnterpriseForm] = Form.useForm(); // 添加企业表单

  const isType3 = type === '3';
  const isPersonalUser = userType !== 3;

  // 生成本地临时 ID（用于手动添加的企业）
  const generateLocalId = () => `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // 判断是否为本地临时 ID
  const isLocalId = (id) => typeof id === 'string' && id.startsWith('local_');

  const { data: area = [] } = useRequest(getArea); // 区域
  const { data: dict = {} } = useRequest(getDictType, { // 筛选项字典
    defaultParams: [defaultParams],
  });

  useRequest(getProjectName, {
    onSuccess(res) {
      // 从URL参数填充项目信息
      const projectInfo = res.find((item) => item.billionAgriProjectId === Number(billionAgriProjectId));
      if (projectInfo) {
        form.setFieldsValue({
          industryChainBelongs: projectInfo.industryChain,
          projectName: projectInfo.projectName,
          applicantNo: isPersonalUser ? user.idcardNo : user.displayCode,
          applicantName: isPersonalUser ? user.realName : user.displayName,
          legalMan: !isPersonalUser ? user.realName : undefined,
        });
      }
    },
  });

  // 获取上下游企业列表
  useRequest(getApplicantList, {
    onSuccess(res) {
      // 格式化为选项格式，显示名称（统一代码）
      const formattedList = res.map((item) => ({
        ...item,
        // 保留原始字段供后续使用
        label: `${item.applicantName}（${item.applicantNo}）`,
        value: item.applicantId,
      }));
      setEnterpriseList(formattedList);
    },
  });

  // 是否选项
  const yesNoOptions = [
    { label: '是', value: 1 },
    { label: '否', value: 0 },
  ];

  const { loading: applyLoading, run: backRun } = useRequest(() => getApplicationDetail(backId), {
    manual: true,
    onSuccess(res) {
      if (res) {
        const cooperationAgreementFileList = [];
        const appendixFileList = [];

        // 处理与关联企业合作协议文件回显
        if (!isEmpty(res?.cooperationAgreementFiles)) {
          // eslint-disable-next-line array-callback-return
          res.cooperationAgreementFiles.map((item, index) => {
            cooperationAgreementFileList.push({
              uid: index.toString(),
              name: item.fileName,
              status: 'done',
              url: item.fileUrl,
            });
          });
          setCooperationAgreementFiles(cooperationAgreementFileList);
        }

        // 处理其他必要材料文件回显
        if (!isEmpty(res?.appendixFiles)) {
          // eslint-disable-next-line array-callback-return
          res.appendixFiles.map((item, index) => {
            appendixFileList.push({
              uid: index.toString(),
              name: item.fileName,
              status: 'done',
              url: item.fileUrl,
            });
          });
          setAppendixFiles(appendixFileList);
        }

        // 处理是否有上下游企业的状态
        if (res.isDown !== undefined) {
          setIsDown(res.isDown === 1);
        }
        if (res.isUp !== undefined) {
          setIsUp(res.isUp === 1);
        }

        // 处理下游企业回显：格式化为与接口一致的格式
        if (res.downstreamEnterprises && res.downstreamEnterprises.length > 0) {
          const formattedDownstream = res.downstreamEnterprises.map((item) => ({
            ...item,
            label: `${item.applicantName}（${item.applicantNo}）`,
            value: item.applicantId,
          }));
          setSelectedDownstream(formattedDownstream);
        }

        // 处理上游企业回显：格式化为与接口一致的格式
        if (res.uptreamEnterprises && res.uptreamEnterprises.length > 0) {
          const formattedUpstream = res.uptreamEnterprises.map((item) => ({
            ...item,
            label: `${item.applicantName}（${item.applicantNo}）`,
            value: item.applicantId,
          }));
          setSelectedUpstream(formattedUpstream);
        }

        // 处理主体角色：将字符串分割为数组
        let mainRoleCodeArray = [];
        if (res.mainRoleCode) {
          mainRoleCodeArray = res.mainRoleCode.split(',');
        }

        const obj = {
          ...res,
          areaId: [res.provinceId, res.cityId, res.areaId],
          registerTime: res.registerTime && moment(res.registerTime, 'YYYY-MM-DD'),
          mainRoleCode: mainRoleCodeArray,
          // 将下游企业转换为ID数组用于表单回显
          downstreamEnterprises: res.downstreamEnterprises?.map((item) => item.applicantId),
          // 将上游企业转换为ID数组用于表单回显
          uptreamEnterprises: res.uptreamEnterprises?.map((item) => item.applicantId),
        };

        // 处理 chainBusinessInfoVos 业务往来信息回显（backId 存在时使用）
        if (res.chainBusinessInfoVos && res.chainBusinessInfoVos.length > 0) {
          const businessInfoMap = {};
          res.chainBusinessInfoVos.forEach((businessInfo) => {
            // 将业务往来信息存储到映射表中
            businessInfoMap[businessInfo.applicantId] = {
              orders: businessInfo.orders || [],
              invoices: businessInfo.invoices || [],
            };

            // 订单总金额
            if (businessInfo.orderTotalAmount !== undefined) {
              obj[`orderAmount_${businessInfo.applicantId}`] = businessInfo.orderTotalAmount;
            }
            // 发票总金额
            if (businessInfo.invoiceTotalAmount !== undefined) {
              obj[`invoiceAmount_${businessInfo.applicantId}`] = businessInfo.invoiceTotalAmount;
            }
            // 订单表格数据
            if (businessInfo.orders && businessInfo.orders.length > 0) {
              obj[`orders_${businessInfo.applicantId}`] = businessInfo.orders;
            }
            // 发票表格数据
            if (businessInfo.invoices && businessInfo.invoices.length > 0) {
              obj[`invoices_${businessInfo.applicantId}`] = businessInfo.invoices;
            }
          });
          // 设置业务往来信息映射表
          setChainBusinessInfoMap(businessInfoMap);
        }

        form.setFieldsValue(obj);
      }
    },
  });

  const { loading, run } = useRequest(addApply, {
    manual: true,
    onSuccess() {
      Navigate('/supplyChain/declareResult');
    },
  });

  const { loading: updateLoading, run: updateRun } = useRequest(updateBackApply, {
    manual: true,
    onSuccess() {
      Navigate('/supplyChain/declareResult');
    },
  });

  const props = {
    name: 'file',
    accept: '.rar,.zip,.doc,.docx,.pdf',
    action: uploadFileUrl,
    headers: {
      authorization: 'authorization-text',
    },
    beforeUpload(file) {
      const isLt2M = file.size / 1024 / 1024 < 10;
      if (!isLt2M) {
        message.error('请上传10M以下文件!');
      }
      return isLt2M;
    },
    // onChange({ file, fileList }) {
    //   if (isEmpty(fileList)) {
    //     form.setFieldsValue({ projectFiles: undefined });
    //   }
    //   setList(fileList);
    //   if (file.status === 'done') {
    //     message.success('上传成功');
    //   } else if (file.status === 'error') {
    //     message.error('上传失败');
    //   }
    // },
  };

  useEffect(() => {
    // type=3（相关经营主体）允许个人用户和企业用户访问
    // type=1（链主企业）和type=2（链属企业）只允许企业用户访问
    if (type !== '3' && userType !== 3) {
      Navigate('/home');
    }
    if (backId) {
      backRun(backId);
    }
  }, []);

  // 处理主体角色字段：将角色代码和名称转换为字符串格式
  function processMainRoleFields(mainRoleCode) {
    if (!mainRoleCode) {
      return null;
    }

    const isArray = Array.isArray(mainRoleCode);
    const codeArray = isArray ? mainRoleCode : [mainRoleCode];
    const roleDict = dict?.[SPC_COMPANY_ROLE] || [];

    return {
      mainRoleCode: codeArray.join(','),
      mainRoleValue: codeArray
        .map((code) => roleDict.find((item) => item.val === code)?.name || '')
        .filter(Boolean)
        .join(','),
    };
  }

  // 构建供应链业务信息数组
  function buildChainBusinessInfoVos(values) {
    const chainBusinessInfoVos = [];
    const allEnterprises = [...selectedDownstream, ...selectedUpstream];
    // 创建values的浅拷贝，避免修改原参数
    const valuesCopy = { ...values };

    allEnterprises.forEach((enterprise) => {
      const orderAmountKey = `orderAmount_${enterprise.applicantId}`;
      const invoiceAmountKey = `invoiceAmount_${enterprise.applicantId}`;
      const ordersKey = `orders_${enterprise.applicantId}`;
      const invoicesKey = `invoices_${enterprise.applicantId}`;

      const chainBusinessInfo = {
        applicantName: enterprise.applicantName,
        applicantNo: enterprise.applicantNo,
        // 手动添加的企业 applicantId 设为 -1，从接口获得的企业使用原有的 applicantId
        applicantId: isLocalId(enterprise.applicantId) ? -1 : enterprise.applicantId,
      };

      // 添加订单和发票总金额（使用后端期望的字段名）
      if (valuesCopy[orderAmountKey] !== undefined && valuesCopy[orderAmountKey] !== null) {
        chainBusinessInfo.orderTotalAmount = valuesCopy[orderAmountKey];
      }
      if (valuesCopy[invoiceAmountKey] !== undefined && valuesCopy[invoiceAmountKey] !== null) {
        chainBusinessInfo.invoiceTotalAmount = valuesCopy[invoiceAmountKey];
      }

      // 添加订单和发票表格数据
      if (valuesCopy[ordersKey] && valuesCopy[ordersKey].length > 0) {
        chainBusinessInfo.orders = valuesCopy[ordersKey];
      }
      if (valuesCopy[invoicesKey] && valuesCopy[invoicesKey].length > 0) {
        chainBusinessInfo.invoices = valuesCopy[invoicesKey];
      }

      chainBusinessInfoVos.push(chainBusinessInfo);

      // 删除valuesCopy中的表格数据，避免重复提交
      delete valuesCopy[orderAmountKey];
      delete valuesCopy[invoiceAmountKey];
      delete valuesCopy[ordersKey];
      delete valuesCopy[invoicesKey];
    });

    return { chainBusinessInfoVos, valuesCopy };
  }

  // 处理企业数据：将选中的企业ID数组转换为企业对象数组
  function processEnterpriseData(valuesCopy) {
    const processedValues = { ...valuesCopy };

    // 处理下游企业
    if (processedValues.downstreamEnterprises && Array.isArray(processedValues.downstreamEnterprises)) {
      processedValues.downstreamEnterprises = selectedDownstream.map((enterprise) => {
        const enterpriseData = {
          applicantName: enterprise.applicantName,
          applicantNo: enterprise.applicantNo,
          // 手动添加的企业 applicantId 设为 -1，从接口获得的企业使用原有的 applicantId
          applicantId: isLocalId(enterprise.applicantId) ? -1 : enterprise.applicantId,
        };
        return enterpriseData;
      });
    }

    // 处理上游企业
    if (processedValues.uptreamEnterprises && Array.isArray(processedValues.uptreamEnterprises)) {
      processedValues.uptreamEnterprises = selectedUpstream.map((enterprise) => {
        const enterpriseData = {
          applicantName: enterprise.applicantName,
          applicantNo: enterprise.applicantNo,
          // 手动添加的企业 applicantId 设为 -1，从接口获得的企业使用原有的 applicantId
          applicantId: isLocalId(enterprise.applicantId) ? -1 : enterprise.applicantId,
        };
        return enterpriseData;
      });
    }

    return processedValues;
  }

  function onFinish() {
    form.validateFields().then(() => {
      // 获取所有表单字段值，包括隐藏字段和未验证字段
      const values = form.getFieldsValue(true);
      const mainRoleFields = processMainRoleFields(values.mainRoleCode);

      // 处理ContactInfo数据
      const { chainBusinessInfoVos, valuesCopy } = buildChainBusinessInfoVos(values);

      // 处理企业数据：将ID数组转换为企业对象数组
      const finalValues = processEnterpriseData(valuesCopy);

      const obj = {
        ...finalValues,
        provinceId: finalValues.areaId[0],
        cityId: finalValues.areaId[1],
        areaId: finalValues.areaId[2],
        registerTime: finalValues.registerTime ? moment(finalValues.registerTime).format('YYYY-MM-DD') : null,
        id: Number(backId),
        billionAgriProjectId: Number(billionAgriProjectId),
        type: Number(type),
        ...(mainRoleFields && mainRoleFields),
      };

      // 处理文件上传：使用 state 中的文件列表而不是表单值
      if (!isEmpty(cooperationAgreementFiles)) {
        obj.cooperationAgreementFiles = getFileListVal({ fileList: cooperationAgreementFiles });
      }
      if (!isEmpty(appendixFiles)) {
        obj.appendixFiles = getFileListVal({ fileList: appendixFiles });
      }

      // 处理业务往来信息：编辑模式和新增模式都使用从表单构建的最新业务往来信息
      if (chainBusinessInfoVos.length > 0) {
        obj.chainBusinessInfoVos = chainBusinessInfoVos;
      }

      if (backId) {
        // 退回记录从新申请
        // 根据 reDeclare 参数设置 editType：true 则为 1（重新申报），否则为 0（编辑）
        obj.editType = reDeclare === 'true' ? 1 : 0;
        updateRun(obj);
      } else {
        // 新申请
        run(obj);
      }
    });
  }

  function checkAuthorize(_, value) {
    const promise = Promise;
    if (!value) {
      return promise.reject('请勾选同意承诺函');
    }
    return promise.resolve();
  }

  // 生成验证规则
  const generateValidationRules = (enterpriseType) => [
    {
      validator: (_, value) => {
        const applicantName = addEnterpriseForm.getFieldValue('applicantName');

        // 规则1: 不能添加当前用户的企业
        if (applicantName && value) {
          const isCurrentUser = applicantName === user.displayName
            && value === (isPersonalUser ? user.idcardNo : user.displayCode);
          if (isCurrentUser) {
            return Promise.reject(new Error('不能添加当前用户所在的企业'));
          }
        }

        // 规则2: 不能添加备选项中已有的企业
        const existingEnterprise = enterpriseList.find(
          (item) => item.applicantName === applicantName && item.applicantNo === value,
        );
        if (existingEnterprise) {
          return Promise.reject(new Error('该企业已在备选列表中，不能重复添加'));
        }

        // 规则3: 如果在上游企业中已添加，则下游企业不能添加；如果在下游企业中已添加，则上游企业不能添加
        if (enterpriseType === 'downstream') {
          const inUpstream = selectedUpstream.find(
            (item) => item.applicantName === applicantName && item.applicantNo === value,
          );
          if (inUpstream) {
            return Promise.reject(new Error('该企业已在上游企业中，不能同时添加到下游企业'));
          }
        } else if (enterpriseType === 'upstream') {
          const inDownstream = selectedDownstream.find(
            (item) => item.applicantName === applicantName && item.applicantNo === value,
          );
          if (inDownstream) {
            return Promise.reject(new Error('该企业已在下游企业中，不能同时添加到上游企业'));
          }
        }

        return Promise.resolve();
      },
    },
  ];

  // 打开添加企业弹窗
  const handleOpenAddEnterprise = (enterpriseType) => {
    setAddEnterpriseType(enterpriseType);
    setAddEnterpriseModal(true);
  };

  // 处理添加企业
  const handleAddEnterprise = () => {
    addEnterpriseForm.validateFields().then((values) => {
      const localId = generateLocalId();
      const newEnterprise = {
        applicantId: localId,
        applicantName: values.applicantName,
        applicantNo: values.applicantNo,
        label: `${values.applicantName}（${values.applicantNo}）`,
        value: localId,
        isLocal: true, // 标记为本地添加的企业
      };

      // 添加到企业列表
      setEnterpriseList([...enterpriseList, newEnterprise]);

      // 根据类型添加到对应的选中列表
      if (addEnterpriseType === 'downstream') {
        const newSelected = [...selectedDownstream, newEnterprise];
        setSelectedDownstream(newSelected);
        // 更新表单值
        form.setFieldsValue({
          downstreamEnterprises: newSelected.map((item) => item.value),
        });
      } else if (addEnterpriseType === 'upstream') {
        const newSelected = [...selectedUpstream, newEnterprise];
        setSelectedUpstream(newSelected);
        // 更新表单值
        form.setFieldsValue({
          uptreamEnterprises: newSelected.map((item) => item.value),
        });
      }

      // 关闭弹窗并重置表单
      setAddEnterpriseModal(false);
      addEnterpriseForm.resetFields();
      message.success('添加成功');
    });
  };

  return (
    <div className="supply-chain-finance__info">
      <div className="content-width">
        <div className="supply-chain-finance__info-title">{titleMap[type]}</div>
        <div className="margin-top20">
          <Spin spinning={applyLoading}>
            <Form
              {...formItemLayout}
              form={form}
              name="form"
              className="form"
              onFinish={onFinish}
            >
              <Card title="基本信息" className="form-info__box">
                <Row>
                  <Col span={12}>
                    <Form.Item
                      name="applicantName"
                      label={isType3 ? '企业名称/姓名' : '企业名称'}
                      rules={[
                        {
                          required: true,
                          message: isType3 ? '请输入企业名称/姓名' : '请输入企业名称',
                        },
                      ]}
                    >
                      <Input placeholder={isType3 ? '请输入企业名称/姓名' : '请输入企业名称'} disabled />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="applicantNo"
                      label={isType3 ? '统一社会信用代码/身份证号码' : '统一社会信用代码'}
                      rules={[
                        {
                          required: true,
                          message: isType3 ? '请输入统一社会信用代码/身份证号码' : '请输入统一社会信用代码',
                        },
                      ]}
                    >
                      <Input placeholder={isType3 ? '请输入统一社会信用代码/身份证号码' : '请输入统一社会信用代码'} disabled />
                    </Form.Item>
                  </Col>
                  <Col span={12} hidden={isType3 && isPersonalUser}>
                    <Form.Item
                      name="registerTime"
                      label="注册时间"
                      rules={[
                        {
                          required: !(isType3 && isPersonalUser),
                          message: '请选择注册时间',
                        },
                      ]}
                    >
                      <DatePicker
                        placeholder="请选择注册时间"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="areaId"
                      label="所在区域"
                      rules={[
                        {
                          required: true,
                          message: '请选择所在区域',
                        },
                      ]}
                    >
                      <Cascader options={area} placeholder="请选择所在区域" fieldNames={{ label: 'name', value: 'id', children: 'children' }} />
                    </Form.Item>
                  </Col>
                  <Col span={12} hidden={isType3 && isPersonalUser}>
                    <Form.Item
                      name="legalMan"
                      label="企业法人"
                      rules={[
                        { required: !(isType3 && isPersonalUser), message: '请输入企业法人' },
                        { whitespace: true },
                      ]}
                    >
                      <Input placeholder="请输入企业法人" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="contactName"
                      label="联系人"
                      rules={[
                        { required: true, message: '请输入联系人' },
                        { whitespace: true },
                      ]}
                    >
                      <Input placeholder="请输入项目联系人" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="contactPhone"
                      label="手机号"
                      rules={[
                        {
                          required: true,
                          message: '请输入手机号',
                        },
                        { pattern: /^(13[0-9]|14[01456879]|15[0-35-9]|16[2567]|17[0-8]|18[0-9]|19[0-35-9])\d{8}$/, message: '请输入正确的手机号码格式' },
                      ]}
                    >
                      <Input placeholder="请输入手机号" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="address"
                      label={isType3 && isPersonalUser ? '联系地址' : '企业详细地址'}
                      rules={[
                        { required: true, message: isType3 && isPersonalUser ? '请输入联系地址' : '请输入企业详细地址' },
                        { whitespace: true },
                      ]}
                    >
                      <Input placeholder={isType3 && isPersonalUser ? '请输入联系地址' : '请输入企业详细地址'} />
                    </Form.Item>
                  </Col>
                  {type === '1' && (
                    <>
                      <Col span={12}>
                        <Form.Item
                          name="industryChainBelongs"
                          label="所属产业链"
                          rules={[
                            { required: true, message: '请输入所属产业链' },
                          ]}
                        >
                          <Input placeholder="请输入所属产业链" disabled />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="projectName"
                          label="项目名称"
                          rules={[
                            { required: true, message: '请输入项目名称' },
                            { whitespace: true },
                          ]}
                        >
                          <Input placeholder="请输入项目名称" disabled />
                        </Form.Item>
                      </Col>
                    </>
                  )}
                  <Col span={24}>
                    <Form.Item
                      labelCol={{ sm: { span: 4 } }}
                      wrapperCol={{ sm: { span: 20 } }}
                      name="mianBusiness"
                      label="主营业务"
                      rules={[
                        { required: true, message: '请输入主营业务' },
                        { whitespace: true },
                      ]}
                    >
                      <Input.TextArea maxLength={1000} showCount placeholder="请输入主营业务" />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      labelCol={{ sm: { span: 4 } }}
                      wrapperCol={{ sm: { span: 20 } }}
                      name="remark"
                      label="备注"
                    >
                      <Input.TextArea maxLength={1000} showCount placeholder="请输入备注" />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
              {
                !isType3 && (
                  <Card title="企业经营信息" className="form-info__box">
                    <Row>
                      <Col span={12}>
                        <Form.Item
                          name="lastYearEmployeeNum"
                          label="上年度职工人数"
                          rules={[
                            { required: true, message: '请输入上年度职工人数' },
                          ]}
                        >
                          <InputNumber
                            placeholder="请输入上年度职工人数"
                            addonAfter="人"
                            style={{ width: '100%' }}
                            min={0}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="lastYearRevenue"
                          label="上年度营收总额"
                          rules={[
                            { required: true, message: '请输入上年度营收总额' },
                            {
                              validator: (_, value) => {
                                if (value && value < 0) {
                                  return Promise.reject(new Error('上年度营收总额必须为正数'));
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <InputNumber
                            placeholder="请输入上年度营收总额"
                            addonAfter="万元"
                            style={{ width: '100%' }}
                            min={0}
                            precision={2}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="lastYearTotalProfit"
                          label="上年度利润总额"
                          rules={[
                            { required: true, message: '请输入上年度利润总额' },
                          ]}
                        >
                          <InputNumber
                            placeholder="请输入上年度利润总额"
                            addonAfter="万元"
                            style={{ width: '100%' }}
                            precision={2}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="lastYearTotalAssets"
                          label="上年度资产总额"
                          rules={[
                            { required: true, message: '请输入上年度资产总额' },
                            {
                              validator: (_, value) => {
                                if (value && value < 0) {
                                  return Promise.reject(new Error('上年度资产总额必须为正数'));
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <InputNumber
                            placeholder="请输入上年度资产总额"
                            addonAfter="万元"
                            style={{ width: '100%' }}
                            min={0}
                            precision={2}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                )
              }
              {
                type !== '1' && (
                  <>
                    <Card title="供应链基本信息" className="form-info__box">
                      <Row>
                        <Col span={10}>
                          <Form.Item
                            name="industryChainBelongs"
                            label="所属产业链"
                            rules={[
                              { required: true, message: '请输入所属产业链' },
                            ]}
                          >
                            <Input placeholder="请输入所属产业链" disabled />
                          </Form.Item>
                        </Col>
                        <Col span={14}>
                          <Form.Item
                            name="projectName"
                            label="项目名称"
                            rules={[
                              { required: true, message: '请输入项目名称' },
                              { whitespace: true },
                            ]}
                          >
                            <Input placeholder="请输入项目名称" disabled />
                          </Form.Item>
                        </Col>
                        <Col span={10}>
                          <Form.Item
                            name="isDown"
                            label="是否有下游企业"
                            rules={[
                              { required: true, message: '请选择是否有下游企业' },
                            ]}
                          >
                            <Select
                              placeholder="请选择是否有下游企业"
                              options={yesNoOptions}
                              disabled={!!backId}
                              onChange={(value) => {
                                setIsDown(value === 1);
                                if (!value) {
                                  // 清空下游企业选择
                                  form.setFieldsValue({ downstreamEnterprises: undefined });
                                  // 清空已选择的下游企业列表，隐藏业务往来信息区域
                                  const currentDownstream = [...selectedDownstream];
                                  currentDownstream.forEach((enterprise) => {
                                    // 清空对应的业务往来信息字段
                                    form.setFieldsValue({
                                      [`orderAmount_${enterprise.applicantId}`]: undefined,
                                      [`invoiceAmount_${enterprise.applicantId}`]: undefined,
                                      [`orders_${enterprise.applicantId}`]: undefined,
                                      [`invoices_${enterprise.applicantId}`]: undefined,
                                    });
                                  });
                                  setSelectedDownstream([]);
                                }
                              }}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={14}>
                          {isDown && (
                            <Form.Item
                              name="downstreamEnterprises"
                              label="下游企业"
                              rules={[
                                { required: true, message: '请选择下游企业' },
                              ]}
                            >
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <Select
                                  mode="multiple"
                                  placeholder="请选择下游企业"
                                  value={selectedDownstream.map((item) => item.value)}
                                  options={backId ? selectedDownstream : enterpriseList.filter((item) => !selectedUpstream.find((up) => up.value === item.value))}
                                  fieldNames={{ label: 'label', value: 'value' }}
                                  showSearch
                                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                                  disabled={!!backId}
                                  onChange={(values) => {
                                    const selected = enterpriseList.filter((item) => values.includes(item.value));
                                    setSelectedDownstream(selected);
                                    // 同时更新表单字段值，确保表单验证能通过
                                    form.setFieldsValue({
                                      downstreamEnterprises: values,
                                    });
                                  }}
                                  style={{ flex: 1 }}
                                />
                                {!backId && (
                                  <Button
                                    type="primary"
                                    onClick={() => handleOpenAddEnterprise('downstream')}
                                  >
                                    添加
                                  </Button>
                                )}
                              </div>
                            </Form.Item>
                          )}
                        </Col>
                        <Col span={10}>
                          <Form.Item
                            name="isUp"
                            label="是否有上游企业"
                            rules={[
                              { required: true, message: '请选择是否有上游企业' },
                            ]}
                          >
                            <Select
                              placeholder="请选择是否有上游企业"
                              options={yesNoOptions}
                              disabled={!!backId}
                              onChange={(value) => {
                                setIsUp(value === 1);
                                if (!value) {
                                  // 清空上游企业选择
                                  form.setFieldsValue({ uptreamEnterprises: undefined });
                                  // 清空已选择的上游企业列表，隐藏业务往来信息区域
                                  const currentUpstream = [...selectedUpstream];
                                  currentUpstream.forEach((enterprise) => {
                                    // 清空对应的业务往来信息字段
                                    form.setFieldsValue({
                                      [`orderAmount_${enterprise.applicantId}`]: undefined,
                                      [`invoiceAmount_${enterprise.applicantId}`]: undefined,
                                      [`orders_${enterprise.applicantId}`]: undefined,
                                      [`invoices_${enterprise.applicantId}`]: undefined,
                                    });
                                  });
                                  setSelectedUpstream([]);
                                }
                              }}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={14}>
                          {isUp && (
                            <Form.Item
                              name="uptreamEnterprises"
                              label="上游企业"
                              rules={[
                                { required: true, message: '请选择上游企业' },
                              ]}
                            >
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <Select
                                  mode="multiple"
                                  placeholder="请选择上游企业"
                                  value={selectedUpstream.map((item) => item.value)}
                                  options={backId ? selectedUpstream : enterpriseList.filter((item) => !selectedDownstream.find((down) => down.value === item.value))}
                                  fieldNames={{ label: 'label', value: 'value' }}
                                  showSearch
                                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                                  disabled={!!backId}
                                  onChange={(values) => {
                                    const selected = enterpriseList.filter((item) => values.includes(item.value));
                                    setSelectedUpstream(selected);
                                    // 同时更新表单字段值，确保表单验证能通过
                                    form.setFieldsValue({
                                      uptreamEnterprises: values,
                                    });
                                  }}
                                  style={{ flex: 1 }}
                                />
                                {!backId && (
                                  <Button
                                    type="primary"
                                    onClick={() => handleOpenAddEnterprise('upstream')}
                                  >
                                    添加
                                  </Button>
                                )}
                              </div>
                            </Form.Item>
                          )}
                        </Col>
                        <Col span={10}>
                          <Form.Item
                            name="mainRoleCode"
                            label="主体角色"
                            rules={[
                              {
                                required: true,
                                message: '请选择主体角色',
                              },
                            ]}
                          >
                            <Select mode="multiple" placeholder="请选择主体角色">
                              {
                                dict?.[SPC_COMPANY_ROLE]?.map((item) => (
                                  <Select.Option value={item.val} key={item.val}>{item.name}</Select.Option>
                                ))
                              }
                            </Select>
                          </Form.Item>
                        </Col>

                        {selectedDownstream.map((enterprise) => (
                          <ContactInfo
                            key={enterprise.applicantId}
                            name={enterprise.applicantName}
                            id={enterprise.applicantId}
                            formRef={form}
                            initialOrderData={chainBusinessInfoMap[enterprise.applicantId]?.orders}
                            initialInvoiceData={chainBusinessInfoMap[enterprise.applicantId]?.invoices}
                          />
                        ))}
                        {selectedUpstream.map((enterprise) => (
                          <ContactInfo
                            key={enterprise.applicantId}
                            name={enterprise.applicantName}
                            id={enterprise.applicantId}
                            formRef={form}
                            initialOrderData={chainBusinessInfoMap[enterprise.applicantId]?.orders}
                            initialInvoiceData={chainBusinessInfoMap[enterprise.applicantId]?.invoices}
                          />
                        ))}
                      </Row>
                    </Card>
                    <Card title="附件材料" className="form-info__box">
                      <Row>
                        <Col span={24}>
                          <Form.Item
                            {...formItemLayout1}
                            name="cooperationAgreementFiles"
                            label={(
                              <div>
                                与关联企业合作协议
                                <div className="sub-label">(需明确合作期限、合作内容等)</div>
                              </div>
                            )}
                          >
                            <Upload
                              {...props}
                              fileList={cooperationAgreementFiles}
                              onChange={(info) => {
                                setCooperationAgreementFiles(info.fileList);
                                if (info.file.status === 'done') {
                                  message.success('上传成功');
                                } else if (info.file.status === 'error') {
                                  message.error('上传失败');
                                }
                              }}
                            >
                              <Button icon={<UploadOutlined />}>文件上传</Button>
                            </Upload>
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          <Form.Item
                            {...formItemLayout1}
                            name="appendixFiles"
                            label="其他必要材料"
                          >
                            <Upload
                              {...props}
                              fileList={appendixFiles}
                              onChange={(info) => {
                                setAppendixFiles(info.fileList);
                                if (info.file.status === 'done') {
                                  message.success('上传成功');
                                } else if (info.file.status === 'error') {
                                  message.error('上传失败');
                                }
                              }}
                            >
                              <Button icon={<UploadOutlined />}>文件上传</Button>
                            </Upload>
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  </>

                )
              }
              <div className="authorize">
                <Form.Item
                  name="authorize"
                  wrapperCol={{ span: 24 }}
                  valuePropName="checked"
                  rules={[
                    { validator: checkAuthorize },
                  ]}

                >
                  <Checkbox onChange={(e) => {
                    form.setFieldsValue({ authorize: false });
                    if (e.target.checked) {
                      setAuthInfoModal(true);
                    }
                  }}
                  >
                    同意并接受
                    <Button type="link" className="authorize-name" onClick={() => setAuthInfoModal(true)}>《承诺函》</Button>
                  </Checkbox>
                </Form.Item>
              </div>
              <div className="submit-btn-box">
                <Button type="primary" htmlType="submit" loading={loading || updateLoading}>
                  提交申报
                </Button>
              </div>
            </Form>

          </Spin>
          <Modal
            title="承诺函"
            open={authInfoModal}
            footer={
              [
                <Button
                  type="primary"
                  key="confirm"
                  onClick={() => {
                    setAuthInfoModal(false);
                    form.setFieldsValue({ authorize: true });
                  }}
                >
                  确认
                </Button>,
              ]
            }
            onCancel={() => setAuthInfoModal(false)}
          >
            <div>
              本人（企业、合作社） 承诺在申报时提交的所有材料真实、合法、有效，若发现材料存在虚假、瞒报等情况， 本人（企业、合作社）自愿放弃本次申报，并承担违规违法责任。
            </div>
          </Modal>
          <AddEnterpriseModal
            open={addEnterpriseModal}
            onOk={handleAddEnterprise}
            onCancel={() => {
              setAddEnterpriseModal(false);
              addEnterpriseForm.resetFields();
            }}
            form={addEnterpriseForm}
            validationRules={generateValidationRules(addEnterpriseType)}
          />
        </div>
      </div>

    </div>
  );
}
